/**
 * ניהול מדיה למוצר או לואריאנט ספציפי — TASKS 3.4.
 *
 * ⭐ רכיב אחד, משמש גם ברמת המוצר (`variantId` לא מועבר) וגם בתוך כל שורת
 *    ואריאנט ב-`ProductFormPage` (`variantId` מועבר) — ההבדל היחיד הוא ה-scope
 *    שנשלח לשרת ושמסנן את התצוגה.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { isAdminApiError } from './adminApi.js';
import { deleteMedia, listMedia, updateMedia, uploadMedia, type MediaItem } from './mediaApi.js';

export function MediaManager({
  productId,
  variantId,
  title,
}: {
  productId: number;
  variantId?: number;
  title: string;
}) {
  const [allMedia, setAllMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [alt, setAlt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  async function refetch() {
    try {
      setAllMedia(await listMedia(productId));
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בטעינת המדיה.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refetch();
  }, [productId]);

  const scopedMedia = allMedia.filter((m) =>
    variantId === undefined ? m.variant_id === null : m.variant_id === variantId,
  );
  // ⚠️ סיומת קובץ בלבד — אינדיקציה ל-UI על אילו שדות להציג, ⛔ לא ולידציה
  //    (הוולידציה האמיתית בשרת, לפי בתי הפתיחה של הקובץ).
  const looksLikeVideo = file?.type.startsWith('video/') ?? false;

  async function handleUpload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!file) {
      setError('יש לבחור קובץ.');
      return;
    }
    setUploading(true);
    try {
      await uploadMedia(productId, {
        file,
        posterFile: posterFile ?? undefined,
        alt: alt.trim(),
        variantId,
      });
      setFile(null);
      setPosterFile(null);
      setAlt('');
      await refetch();
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בהעלאת הקובץ.');
    } finally {
      setUploading(false);
    }
  }

  async function handleSetPrimary(mediaId: number) {
    setError(null);
    try {
      await updateMedia(productId, mediaId, { isPrimary: true });
      await refetch();
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בעדכון.');
    }
  }

  async function handleDelete(mediaId: number) {
    setError(null);
    try {
      await deleteMedia(productId, mediaId);
      setConfirmDeleteId(null);
      await refetch();
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה במחיקה.');
    }
  }

  return (
    <section className="border-line mt-4 rounded-lg border p-4" aria-label={title}>
      <h3 className="text-sm font-semibold">{title}</h3>

      {error && (
        <p role="alert" className="text-danger mt-2 text-sm">
          {error}
        </p>
      )}

      {loading && <p className="text-muted mt-2 text-sm">טוען…</p>}

      {!loading && scopedMedia.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-3">
          {scopedMedia.map((item) => (
            <li key={item.id} className="w-32 text-center text-xs">
              {/* ⭐ DESIGN.md 8.4 — "תמונה ראשית מסומנת בתג מסגרת נחושת". */}
              <div
                className={`relative h-24 w-32 overflow-hidden rounded ${
                  item.is_primary ? 'border-copper border-2' : 'border-line border'
                }`}
              >
                {item.type === 'image' ? (
                  <img
                    src={`/${item.url}`}
                    alt={item.alt_text ?? ''}
                    width={item.width ?? undefined}
                    height={item.height ?? undefined}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <video
                    src={`/${item.url}`}
                    poster={item.poster_url ? `/${item.poster_url}` : undefined}
                    controls
                    preload="none"
                    className="h-full w-full"
                  >
                    <track kind="captions" />
                  </video>
                )}
                {item.is_primary && (
                  <span className="bg-canvas border-copper text-ink absolute start-1 top-1 rounded border px-1 text-[10px] font-bold">
                    ראשית
                  </span>
                )}
              </div>

              <p className="mt-1 truncate" title={item.alt_text ?? ''}>
                {item.alt_text}
              </p>

              <div className="mt-1 flex items-center justify-center gap-1">
                <button
                  type="button"
                  className="rounded border border-line px-1"
                  disabled={Boolean(item.is_primary)}
                  onClick={() => void handleSetPrimary(item.id)}
                >
                  {item.is_primary ? 'זו הראשית' : 'הפוך לראשית'}
                </button>

                {confirmDeleteId === item.id ? (
                  <>
                    <button
                      type="button"
                      className="text-danger rounded border border-line px-1"
                      onClick={() => void handleDelete(item.id)}
                    >
                      לאשר
                    </button>
                    <button
                      type="button"
                      className="rounded border border-line px-1"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      ביטול
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="rounded border border-line px-1"
                    onClick={() => setConfirmDeleteId(item.id)}
                  >
                    מחיקה
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ⭐ נוסף — פידבק אריאנה: הטופס הזה בלט על רקע התמונות הקיימות בלי שום
          מסגרת או כותרת, קשה היה להבדיל בין "תמונות קיימות" ל"הוספת תמונה
          חדשה". מפריד (border-t) + כותרת + מסגרת סביב כל הטופס, אותו דפוס
          כמו טופס עדכון המחיר שמעליו ב-ProductFormPage. */}
      <div className="border-line mt-4 border-t pt-4">
        <h4 className="text-xs font-semibold">הוספת תמונה או וידאו</h4>
        <form
          className="border-line bg-surface mt-2 grid gap-3 rounded-md border p-3 sm:grid-cols-2"
          onSubmit={(e) => void handleUpload(e)}
        >
          <div>
            <label htmlFor={`file-${title}`} className="block text-xs font-semibold">
              קובץ (תמונה או וידאו)
            </label>
            <input
              id={`file-${title}`}
              type="file"
              required
              accept="image/jpeg,image/png,image/webp,video/mp4"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="bg-canvas mt-1 w-full rounded-md border border-line px-2 py-1 text-sm"
            />
          </div>

          <div>
            <label htmlFor={`alt-${title}`} className="block text-xs font-semibold">
              טקסט חלופי (alt) - חובה
            </label>
            <input
              id={`alt-${title}`}
              required
              aria-required="true"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              className="bg-canvas mt-1 w-full rounded-md border border-line px-2 py-1 text-sm"
            />
          </div>

          {looksLikeVideo && (
            <div className="sm:col-span-2">
              <label htmlFor={`poster-${title}`} className="block text-xs font-semibold">
                תמונת שער (poster) - חובה לווידאו
              </label>
              <input
                id={`poster-${title}`}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setPosterFile(e.target.files?.[0] ?? null)}
                className="bg-canvas mt-1 w-full rounded-md border border-line px-2 py-1 text-sm"
              />
            </div>
          )}

          <div className="sm:col-span-2">
            <button type="submit" className="btn btn-secondary" disabled={uploading}>
              {uploading ? 'מעלה…' : 'העלאה'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
